import { useState, useRef, useCallback, createContext, useContext, ReactNode } from 'react';
import { MDSIcon, MDSPanel, MDSPlainButton, MDSTypography } from '@marqvision/mds-v2';
import { Comment, ListingCommentPanelProps } from '../@types';
import { CommentsView } from './CommentsView';
import { ThreadView } from './ThreadView';
import { Styled } from './styles';

type BaseState = { viewMode: 'comment'; parentComment: null } | { viewMode: 'thread'; parentComment: Comment };

export const ListingCommentPanel = <C extends Comment[], R extends Comment[]>(
  props: ListingCommentPanelProps<C, R>
) => {
  const { appInfo, listingId, apiHook, panel, onSend, onDelete, onSendError } = props;

  const [baseState, setBaseState] = useState<BaseState>({
    viewMode: 'comment',
    parentComment: null,
  });

  const panelContentRef = useRef<HTMLDivElement>(null);
  const threadScrollToBottom = useCallback(() => {
    if (panelContentRef.current) {
      panelContentRef.current.scrollTop = panelContentRef.current.scrollHeight;
    }
  }, []);

  const openThread = async (parentComment: Comment) => {
    setBaseState({
      viewMode: 'thread',
      parentComment,
    });
  };
  const closeThread = () => {
    setBaseState({
      viewMode: 'comment',
      parentComment: null,
    });
  };

  return (
    <MDSPanel.Wrapper isOpen={panel.isOpen} width="544px" onClose={panel.onClose}>
      <MDSPanel.Header onClose={panel.onClose}>
        {baseState.viewMode === 'comment' ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <MDSTypography variant="title" size="l" weight="semibold">
              Comments
            </MDSTypography>
            {!appInfo.hideListingId && (
              <MDSTypography variant="body" size="s" weight="regular" color="color/content/neutral/secondary/normal">
                #{listingId}
              </MDSTypography>
            )}
          </div>
        ) : (
          <Styled.HeaderContainer>
            <MDSPlainButton color="bluegray" icon={<MDSIcon.TailArrowLeft />} onClick={closeThread} />
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <MDSTypography variant="title" size="l" weight="semibold">
                Thread
              </MDSTypography>
              {!appInfo.hideListingId && (
                <MDSTypography variant="body" size="s" weight="regular" color="color/content/neutral/secondary/normal">
                  #{listingId}
                </MDSTypography>
              )}
            </div>
          </Styled.HeaderContainer>
        )}
      </MDSPanel.Header>
      <MDSPanel.Content ref={panelContentRef}>
        {baseState.viewMode === 'comment' ? (
          <CommentsView
            appInfo={appInfo}
            listingId={listingId}
            apiHook={{
              getComments: apiHook.getComments,
              getPresignedUrl: apiHook.getPresignedUrl,
              addComment: apiHook.addComment,
              removeComment: apiHook.removeComment,
            }}
            onOpenThread={openThread}
            onSendSuccess={onSend}
            onSendError={onSendError}
            onDelete={onDelete}
          />
        ) : (
          <ThreadView
            appInfo={appInfo}
            listingId={listingId}
            parentComment={baseState.parentComment}
            apiHook={{
              getReplies: apiHook.getReplies,
              getPresignedUrl: apiHook.getPresignedUrl,
              addComment: apiHook.addComment,
              removeComment: apiHook.removeComment,
            }}
            scrollPositionUpdate={threadScrollToBottom}
            onSendSuccess={onSend}
            onSendError={onSendError}
            onDelete={onDelete}
          />
        )}
      </MDSPanel.Content>
    </MDSPanel.Wrapper>
  );
};

//#region Context version

// Context for sharing state between Header and Content components
type ListingCommentContextType<C extends Comment[] = Comment[], R extends Comment[] = Comment[]> = {
  baseState: BaseState;
  openThread: (parentComment: Comment) => Promise<void>;
  closeThread: () => void;
  threadScrollToBottom: () => void;
  appInfo: ListingCommentPanelProps<C, R>['appInfo'];
  listingId: number;
  apiHook: ListingCommentPanelProps<C, R>['apiHook'];
  panelContentRef: React.RefObject<HTMLDivElement>;
};

const ListingCommentContext = createContext<ListingCommentContextType | null>(null);

const useListingCommentContext = () => {
  const context = useContext(ListingCommentContext);
  if (!context) {
    throw new Error('useListingCommentContext must be used within ListingCommentProvider');
  }
  return context;
};

// Provider component that manages the shared state
export const ListingCommentProvider = <C extends Comment[], R extends Comment[]>({
  children,
  appInfo,
  listingId,
  apiHook,
}: {
  children: ReactNode;
  appInfo: ListingCommentPanelProps<C, R>['appInfo'];
  listingId: number;
  apiHook: ListingCommentPanelProps<C, R>['apiHook'];
}) => {
  const [baseState, setBaseState] = useState<BaseState>({
    viewMode: 'comment',
    parentComment: null,
  });

  const panelContentRef = useRef<HTMLDivElement>(null);
  const threadScrollToBottom = useCallback(() => {
    if (panelContentRef.current) {
      const element = panelContentRef.current;

      // scrollIntoView 사용 (더 안정적)
      const lastChild = element.lastElementChild;
      if (lastChild) {
        lastChild.scrollIntoView({ behavior: 'auto', block: 'end' });
      } else {
        // fallback to scrollTop
        element.scrollTop = element.scrollHeight;
      }
    }
  }, []);

  const openThread = async (parentComment: Comment) => {
    setBaseState({
      viewMode: 'thread',
      parentComment,
    });
  };

  const closeThread = () => {
    setBaseState({
      viewMode: 'comment',
      parentComment: null,
    });
  };

  const contextValue: ListingCommentContextType<C, R> = {
    baseState,
    openThread,
    closeThread,
    threadScrollToBottom,
    appInfo,
    listingId,
    apiHook,
    panelContentRef,
  };

  return <ListingCommentContext.Provider value={contextValue}>{children}</ListingCommentContext.Provider>;
};

export const ListingCommentHeader = () => {
  const { baseState, closeThread } = useListingCommentContext();

  return (
    <>
      {baseState.viewMode === 'comment' ? (
        <MDSTypography variant="title" size="l" weight="semibold">
          Comments
        </MDSTypography>
      ) : (
        <Styled.HeaderContainer>
          <MDSPlainButton color="bluegray" icon={<MDSIcon.TailArrowLeft />} onClick={closeThread} />
          <MDSTypography variant="title" size="l" weight="semibold">
            Thread
          </MDSTypography>
        </Styled.HeaderContainer>
      )}
    </>
  );
};

type ListingCommentContentProps = {
  onSend?: () => void;
  onSendError?: () => void;
  onDelete?: () => void;
};
export const ListingCommentContent = (props: ListingCommentContentProps) => {
  const { onSend, onSendError, onDelete } = props;
  const { baseState, appInfo, listingId, apiHook, openThread, threadScrollToBottom, panelContentRef } =
    useListingCommentContext();

  return (
    <>
      {baseState.viewMode === 'comment' ? (
        <CommentsView
          appInfo={appInfo}
          listingId={listingId}
          apiHook={{
            getComments: apiHook.getComments,
            getPresignedUrl: apiHook.getPresignedUrl,
            addComment: apiHook.addComment,
            removeComment: apiHook.removeComment,
          }}
          onOpenThread={openThread}
          onSendSuccess={onSend}
          onSendError={onSendError}
          onDelete={onDelete}
        />
      ) : (
        <div ref={panelContentRef}>
          <ThreadView
            appInfo={appInfo}
            listingId={listingId}
            parentComment={baseState.parentComment}
            apiHook={{
              getReplies: apiHook.getReplies,
              getPresignedUrl: apiHook.getPresignedUrl,
              addComment: apiHook.addComment,
              removeComment: apiHook.removeComment,
            }}
            scrollPositionUpdate={threadScrollToBottom}
            onSendSuccess={onSend}
            onSendError={onSendError}
            onDelete={onDelete}
          />
        </div>
      )}
    </>
  );
};

// Example usage of the separated components with Context
// You can use this pattern to compose the Header and Content separately

// const ListingCommentWithContext = <C extends Comment[], R extends Comment[]>(
//   props: ListingCommentPanelProps<C, R>
// ) => {
//   const { appInfo, listingId, apiHook } = props;

//   return (
//     <ListingCommentProvider appInfo={appInfo} listingId={listingId} apiHook={apiHook}>
//       {/* You can now use Header and Content separately wherever needed */}
//       <div>
//         <ListingCommentHeader />
//       </div>
//       <div>
//         <ListingCommentContent />
//       </div>
//     </ListingCommentProvider>
//   );
// };
//#endregion
